import React, { useMemo } from "react";

// Spec'd field groups keep the submission scannable at a glance.
const FIELD_GROUPS = [
  {
    title: "Customer",
    fields: [
      ["full_name", "Full name"],
      ["business_name", "Business"],
      ["email", "Email"],
      ["phone", "Phone"],
      ["preferred_contact_method", "Preferred contact"],
      ["current_website_url", "Current site"],
      ["social_media_links", "Social links"],
    ],
  },
  {
    title: "Package",
    fields: [
      ["package_selected", "Package"],
      ["package_price", "Price"],
      ["package_scope_confirmation", "Scope confirmed"],
    ],
  },
  {
    title: "Business Overview",
    fields: [
      ["business_description", "What they do"],
      ["ideal_customer", "Ideal customer"],
      ["problem_solved", "Problem solved"],
      ["services_or_offers", "Services / offers"],
      ["business_differentiator", "Differentiator"],
      ["service_area", "Service area"],
      ["business_type", "Business type"],
      ["competitors", "Competitors"],
    ],
  },
  {
    title: "Website Goal",
    fields: [
      ["website_goal", "Primary goal"],
      ["primary_visitor_action", "Desired action"],
      ["primary_cta_text", "CTA text"],
      ["post_submit_action", "After-submit"],
      ["pricing_visibility", "Show pricing"],
      ["avoid_mentioning", "Avoid"],
    ],
  },
  {
    title: "Brand & Design",
    fields: [
      ["logo_status", "Logo"],
      ["brand_colors_status", "Brand colors"],
      ["brand_colors", "Colors"],
      ["colors_to_avoid", "Avoid colors"],
      ["desired_brand_feel", "Feel"],
      ["visual_direction", "Direction"],
      ["websites_liked", "Liked sites"],
      ["websites_disliked", "Disliked sites"],
    ],
  },
  {
    title: "Content & Assets",
    fields: [
      ["copy_status", "Copy"],
      ["photo_video_status", "Photos/video"],
      ["testimonial_status", "Testimonials"],
      ["testimonials_or_review_links", "Testimonial content"],
      ["google_business_profile_status", "GBP"],
      ["google_business_profile_link", "GBP link"],
    ],
  },
  {
    title: "Technical Setup",
    fields: [
      ["domain_status", "Domain"],
      ["domain_name", "Domain name"],
      ["domain_provider", "Registrar"],
      ["hosting_status", "Hosting"],
      ["business_email_status", "Business email"],
      ["needs_business_email_setup", "Needs email setup"],
      ["needs_domain_connection", "Needs domain connection"],
    ],
  },
  {
    title: "Launch & Approval",
    fields: [
      ["approval_contact", "Approver"],
      ["ideal_launch_date", "Launch target"],
      ["hard_deadlines", "Deadlines"],
      ["feedback_speed", "Feedback speed"],
      ["additional_notes", "Notes"],
      ["timeline_confirmation", "Timeline confirmed"],
      ["authorization_confirmation", "Authorization confirmed"],
    ],
  },
];

const PACKAGE_DETAILS = {
  starter: "starter_website_details",
  growth: "business_website_details",
  premium: "booking_setup_details",
  not_sure: "not_sure_details",
};

const fmtValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) {
    if (v.length === 0) return "—";
    if (typeof v[0] === "object" && v[0]?.url) return v.map((f) => f.url).join(", ");
    return v.join(", ");
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const Row = ({ label, value }) => (
  <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[140px_1fr] gap-3 py-2 border-b border-sand-300/70 last:border-b-0">
    <p className="font-mono text-[10px] sm:text-[10.5px] uppercase tracking-[0.16em] text-ink-muted pt-0.5">{label}</p>
    <p className="text-[13px] sm:text-[13.5px] text-ink leading-relaxed break-words">{fmtValue(value)}</p>
  </div>
);

/** The secondary "Submission details" column: grouped form fields,
 *  package-specific details, and asset links. */
export default function IntakeDetailFields({ doc }) {
  const pkgDetails = useMemo(() => {
    if (!doc?.package_key) return null;
    const key = PACKAGE_DETAILS[doc.package_key];
    return key ? doc[key] : null;
  }, [doc]);

  const assetFiles = [
    ...(doc?.brand_files || []),
    ...(doc?.written_content_files || []),
    ...(doc?.media_files || []),
  ];

  return (
    <section className="rounded-3xl border border-sand-300 bg-sand-50 p-6 sm:p-8" data-testid="intake-detail-fields">
      <h2 className="font-display text-[1.1rem] font-semibold tracking-tight">Submission details</h2>

      {FIELD_GROUPS.map((g) => (
        <div key={g.title} className="mt-6">
          <p className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-clay mb-2">{g.title}</p>
          <div className="rounded-2xl border border-sand-300 bg-sand-100/50 px-4">
            {g.fields.map(([k, label]) => (
              <Row key={k} label={label} value={doc?.[k]} />
            ))}
          </div>
        </div>
      ))}

      {pkgDetails && (
        <div className="mt-6" data-testid="intake-detail-package-specific">
          <p className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-clay mb-2">
            Package-specific ({doc?.package_key})
          </p>
          <div className="rounded-2xl border border-sand-300 bg-sand-100/50 px-4">
            {Object.entries(pkgDetails).map(([k, v]) => (
              <Row key={k} label={k.replace(/_/g, " ")} value={v} />
            ))}
          </div>
        </div>
      )}

      {assetFiles.length ? (
        <div className="mt-6">
          <p className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-clay mb-2">Asset links</p>
          <div className="rounded-2xl border border-sand-300 bg-sand-100/50 p-4 space-y-2">
            {assetFiles.map((f) => (
              <a
                key={f.url || `${f.kind || "file"}:${f.url || ""}`}
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="block text-[13px] text-clay hover:text-rust break-all"
              >
                {f.kind || "file"} → {f.url}
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
